import {
  ChevronDown,
  ChevronRight,
  Clock,
  MessageCircle,
  Play,
  RefreshCw,
  Sparkles,
  Square,
  Target,
} from 'lucide-react-native';
import { Fragment, type ReactElement, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { SkeletonCard } from '../../../components/animations/Skeleton';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import { listDivider } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import {
  useCancelLeadPipeline,
  useExecuteAgent,
  useExecutePipeline,
  useLeadAgentStatus,
} from '../hooks/useAgents';
import { useRefetchOnRegainFocus } from '../hooks/useRefetchOnRegainFocus';
import type {
  AgentExecution,
  EnrichmentResult,
  FollowUpResult,
  QualificationResult,
  SentimentResult,
} from '../types';
import { AgentExecutionStatus, AgentType } from '../types';

interface AgentInsightsCardProps {
  leadId: string;
  organizationId: string;
}

const AGENT_SECTIONS = [
  {
    type: AgentType.LEAD_QUALIFICATION,
    labelKey: 'agents.insights.qualification',
    icon: Target,
    iconColor: colors.primary[500],
    resultKey: 'qualification' as const,
  },
  {
    type: AgentType.LEAD_ENRICHMENT,
    labelKey: 'agents.insights.enrichment',
    icon: Sparkles,
    iconColor: colors.accent.purple[400],
    resultKey: 'enrichment' as const,
  },
  {
    type: AgentType.SENTIMENT_ANALYSIS,
    labelKey: 'agents.insights.sentiment',
    icon: MessageCircle,
    iconColor: colors.accent.blue[400],
    resultKey: 'sentiment' as const,
  },
  {
    type: AgentType.FOLLOW_UP_SCHEDULER,
    labelKey: 'agents.insights.followUp',
    icon: Clock,
    iconColor: colors.status.warning.main,
    resultKey: 'followUp' as const,
  },
] as const;

function getExecutionStatus(
  agentType: AgentType,
  executions: AgentExecution[],
): AgentExecutionStatus | null {
  const latest = executions.find((e) => e.agentType === agentType);
  return latest ? latest.status : null;
}

/**
 * Whether the latest run for this agent produced rule-based output instead of AI
 * output (FLOSEAI-610). A fallback is stored as `status: 'completed'`, so without
 * reading `outcome` the card presented "AI qualified lead as cold" for a result
 * the AI never saw.
 */
function latestExecution(agentType: AgentType, executions: AgentExecution[]) {
  return executions.find((e) => e.agentType === agentType);
}

const TERMINAL_STATUSES: AgentExecutionStatus[] = [
  AgentExecutionStatus.COMPLETED,
  AgentExecutionStatus.FAILED,
  AgentExecutionStatus.CANCELLED,
];

function isRerunSettled(
  rerun: { agentType: AgentType; baselineId: string | null },
  executions: AgentExecution[],
): boolean {
  const latest = latestExecution(rerun.agentType, executions);
  return !!latest && latest.id !== rerun.baselineId && TERMINAL_STATUSES.includes(latest.status);
}

function isDegraded(agentType: AgentType, executions: AgentExecution[]): boolean {
  const latest = executions.find((e) => e.agentType === agentType);
  return latest?.outcome === 'fallback';
}

function StatusBadge({
  status,
  degraded = false,
}: Readonly<{ status: AgentExecutionStatus | null; degraded?: boolean }>) {
  const { t } = useTranslation();

  if (!status) {
    return (
      <Badge color="neutral" variant="subtle" size="sm">
        {t('agents.insights.statusNotRun')}
      </Badge>
    );
  }

  // Degradation outranks the status: "Done" is technically true and practically
  // misleading, because nothing else on the card says the model was skipped.
  if (degraded && status === AgentExecutionStatus.COMPLETED) {
    return (
      <Badge color="amber" variant="subtle" size="sm">
        {t('agents.insights.statusRuleBased')}
      </Badge>
    );
  }

  const config: Record<
    string,
    { color: 'green' | 'amber' | 'red' | 'cyan' | 'neutral'; labelKey: string }
  > = {
    [AgentExecutionStatus.COMPLETED]: { color: 'green', labelKey: 'agents.insights.statusDone' },
    [AgentExecutionStatus.RUNNING]: { color: 'cyan', labelKey: 'agents.insights.statusRunning' },
    [AgentExecutionStatus.QUEUED]: { color: 'amber', labelKey: 'agents.insights.statusQueued' },
    [AgentExecutionStatus.FAILED]: { color: 'red', labelKey: 'agents.insights.statusFailed' },
    [AgentExecutionStatus.CANCELLED]: {
      color: 'neutral',
      labelKey: 'agents.insights.statusCancelled',
    },
  };

  const entry = config[status];

  return (
    <Badge color={entry?.color ?? 'neutral'} variant="subtle" size="sm">
      {entry ? t(entry.labelKey) : status}
    </Badge>
  );
}

const QUALIFICATION_LEVEL_COLORS: Record<string, string> = {
  hot: colors.status.success.main,
  warm: colors.status.warning.main,
};

const QUALIFICATION_BADGE_COLORS: Record<string, 'green' | 'amber' | 'cyan'> = {
  hot: 'green',
  warm: 'amber',
};

const QUALIFICATION_LEVEL_LABEL_KEYS: Record<string, string> = {
  hot: 'leads.enrichment.qualificationHot',
  warm: 'leads.enrichment.qualificationWarm',
  cold: 'leads.enrichment.qualificationCold',
  unqualified: 'leads.enrichment.qualificationUnqualified',
};

function QualificationContent({ result }: Readonly<{ result: QualificationResult }>) {
  const { t } = useTranslation();
  const { reasoning: translatedReasoning, suggestedActions: translatedActions } = useTranslateOnFly(
    {
      reasoning: result.reasoning,
      suggestedActions: result.suggestedActions,
    },
  );

  const qualificationLevel = result.qualificationLevel ?? 'unknown';
  const levelColor = QUALIFICATION_LEVEL_COLORS[qualificationLevel] ?? colors.accent.blue[400];
  const badgeColor = QUALIFICATION_BADGE_COLORS[qualificationLevel] ?? 'cyan';
  const levelLabel = t(
    QUALIFICATION_LEVEL_LABEL_KEYS[qualificationLevel] ?? 'leads.enrichment.qualificationUnknown',
  );

  return (
    <VStack spacing="sm">
      <HStack
        spacing="sm"
        align="center"
        accessible
        accessibilityLabel={`${t('agents.insights.qualificationScoreA11y', { score: result.score })}, ${levelLabel}`}
      >
        <Text variant="caption" color={colors.text.tertiary}>
          {t('agents.insights.qualificationScoreLabel')}
        </Text>
        <Box
          backgroundColor={withAlpha(levelColor, 0.16)}
          borderRadius="sm"
          alignItems="center"
          justifyContent="center"
          style={styles.scoreBadge}
        >
          <Text color={levelColor} style={styles.scoreText}>
            {`${result.score}/100`}
          </Text>
        </Box>
        <Badge color={badgeColor} variant="subtle" size="sm">
          {levelLabel}
        </Badge>
      </HStack>
      {Boolean(result.reasoning) && (
        <Text variant="caption" color={colors.text.secondary} numberOfLines={3}>
          {translatedReasoning ?? result.reasoning}
        </Text>
      )}
      {result.suggestedActions?.length > 0 && (
        <VStack spacing="xs">
          {(translatedActions ?? result.suggestedActions).slice(0, 3).map((action, index) => (
            <HStack key={`action-${index}`} spacing="xs" align="center">
              <Text variant="caption" color={colors.text.tertiary}>
                {'\u2022'}
              </Text>
              <Text
                variant="caption"
                color={colors.text.secondary}
                numberOfLines={1}
                style={{ flex: 1 }}
              >
                {action}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
}

const SENTIMENT_BADGE_COLORS: Record<string, 'green' | 'red' | 'amber'> = {
  very_positive: 'green',
  positive: 'green',
  neutral: 'amber',
  negative: 'red',
  very_negative: 'red',
};

const SENTIMENT_LABEL_KEYS: Record<string, string> = {
  very_positive: 'leads.enrichment.sentimentVeryPositive',
  positive: 'leads.enrichment.sentimentPositive',
  neutral: 'leads.enrichment.sentimentNeutral',
  negative: 'leads.enrichment.sentimentNegative',
  very_negative: 'leads.enrichment.sentimentVeryNegative',
};

const MAX_LIST_ITEMS = 3;
const MAX_ENRICHMENT_FIELDS = 5;
const MAX_ITEM_LINES = 2;
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const KNOWN_CHANNELS = new Set(['email', 'call', 'whatsapp', 'meeting']);
const KNOWN_URGENCIES = new Set(['immediate', 'this_week', 'next_week', 'nurture']);

type Translate = ReturnType<typeof useTranslation>['t'];

function knownLabel(
  t: Translate,
  group: string,
  value: string | null | undefined,
  known: Set<string>,
): string | null {
  return value && known.has(value) ? t(`agents.insights.${group}.${value}`) : null;
}

function parseDisplayDate(value: string): Date {
  return new Date(ISO_DATE_ONLY.test(value) ? `${value}T12:00:00` : value);
}

function formatDate(value: string | undefined): string | null {
  if (!value) return null;
  const date = parseDisplayDate(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function BulletList({
  items,
  prefix,
}: Readonly<{ items: string[]; prefix: string }>): ReactElement | null {
  if (items.length === 0) return null;

  return (
    <VStack spacing="xs">
      {items.map((item, index) => (
        <HStack key={`${prefix}-${index}`} spacing="xs" align="center">
          <Text variant="caption" color={colors.text.tertiary}>
            {'\u2022'}
          </Text>
          <Text
            variant="caption"
            color={colors.text.secondary}
            numberOfLines={MAX_ITEM_LINES}
            style={{ flex: 1 }}
          >
            {item}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
}

function LabeledList({
  label,
  items,
}: Readonly<{ label: string; items: string[] }>): ReactElement | null {
  if (items.length === 0) return null;

  return (
    <VStack spacing="xs">
      <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '600' }}>
        {label}
      </Text>
      <BulletList items={items} prefix={label} />
    </VStack>
  );
}

function EmptyResult({ messageKey }: Readonly<{ messageKey: string }>): ReactElement {
  const { t } = useTranslation();
  return (
    <Text variant="caption" color={colors.text.tertiary}>
      {t(messageKey)}
    </Text>
  );
}

function SentimentContent({ result }: Readonly<{ result: SentimentResult }>): ReactElement {
  const { t } = useTranslation();
  const signalDetails = useMemo(
    () => (result.signals ?? []).map((signal) => signal.detail).filter(Boolean),
    [result.signals],
  );
  const riskFactors = useMemo(() => result.riskFactors ?? [], [result.riskFactors]);
  const opportunities = useMemo(() => result.opportunities ?? [], [result.opportunities]);
  const translated = useTranslateOnFly({
    reasoning: result.reasoning,
    keyInsights: signalDetails,
    painPoints: riskFactors,
    suggestedActions: opportunities,
  });

  const sentiment = result.overall ?? 'neutral';
  const badgeColor = SENTIMENT_BADGE_COLORS[sentiment] ?? 'amber';
  const sentimentLabel = t(SENTIMENT_LABEL_KEYS[sentiment] ?? SENTIMENT_LABEL_KEYS.neutral);
  const hasDetails =
    Boolean(result.reasoning) ||
    signalDetails.length > 0 ||
    riskFactors.length > 0 ||
    opportunities.length > 0;

  return (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center">
        <Badge color={badgeColor} variant="subtle" size="sm">
          {sentimentLabel}
        </Badge>
        <Text variant="caption" color={colors.text.tertiary}>
          {Math.round((result.confidence ?? 0) * 100)}% {t('agents.insights.confidence')}
        </Text>
      </HStack>
      {Boolean(result.reasoning) && (
        <Text variant="caption" color={colors.text.secondary} numberOfLines={3}>
          {translated.reasoning ?? result.reasoning}
        </Text>
      )}
      <BulletList
        items={(translated.keyInsights ?? signalDetails).slice(0, MAX_LIST_ITEMS)}
        prefix="signal"
      />
      <LabeledList
        label={t('agents.insights.risks')}
        items={(translated.painPoints ?? riskFactors).slice(0, MAX_LIST_ITEMS)}
      />
      <LabeledList
        label={t('agents.insights.opportunities')}
        items={(translated.suggestedActions ?? opportunities).slice(0, MAX_LIST_ITEMS)}
      />
      {!hasDetails && <EmptyResult messageKey="agents.insights.sentimentNoSignals" />}
    </VStack>
  );
}

const ENRICHMENT_FIELD_ORDER = [
  'industry',
  'jobTitle',
  'companySize',
  'location',
  'website',
  'revenue',
  'linkedinUrl',
  'timezone',
  'technologies',
] as const;

function enrichmentValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    const items = value.filter((item) => typeof item === 'string' && item.trim() !== '');
    return items.length > 0 ? items.join(', ') : null;
  }
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function EnrichmentContent({ result }: Readonly<{ result: EnrichmentResult }>): ReactElement {
  const { t } = useTranslation();
  const { reasoning: translatedReasoning } = useTranslateOnFly({ reasoning: result.reasoning });
  const found = ENRICHMENT_FIELD_ORDER.flatMap((key) => {
    const value = enrichmentValue(result[key]);
    return value ? [{ key, value }] : [];
  });
  const enrichedOn = formatDate(result.enrichedAt);

  return (
    <VStack spacing="sm">
      {found.length > 0 ? (
        <HStack spacing="sm" align="center">
          <Badge color="purple" variant="subtle" size="sm">
            {t('agents.insights.enrichmentFieldsFound', { count: found.length })}
          </Badge>
          {typeof result.confidence === 'number' && (
            <Text variant="caption" color={colors.text.tertiary}>
              {Math.round(result.confidence * 100)}% {t('agents.insights.confidence')}
            </Text>
          )}
        </HStack>
      ) : (
        <EmptyResult messageKey="agents.insights.enrichmentNothingFound" />
      )}
      {Boolean(result.reasoning) && (
        <Text variant="caption" color={colors.text.secondary} numberOfLines={3}>
          {translatedReasoning ?? result.reasoning}
        </Text>
      )}
      <BulletList
        items={found
          .slice(0, MAX_ENRICHMENT_FIELDS)
          .map(({ key, value }) => `${t(`agents.insights.enrichmentField.${key}`)}: ${value}`)}
        prefix="field"
      />
      {enrichedOn && (
        <Text variant="caption" color={colors.text.tertiary}>
          {t('leads.enrichment.enrichedLabel')}: {enrichedOn}
        </Text>
      )}
    </VStack>
  );
}

function FollowUpContent({ result }: Readonly<{ result: FollowUpResult }>): ReactElement {
  const { t } = useTranslation();
  const steps = useMemo(() => result.recommendations ?? [], [result.recommendations]);
  const stepReasonsSource = useMemo(() => steps.map((step) => step.reasoning ?? ''), [steps]);
  const translated = useTranslateOnFly({
    reasoning: result.reasoning,
    suggestedActions: stepReasonsSource,
  });
  const stepReasons = translated.suggestedActions ?? stepReasonsSource;
  const next = steps[0];
  const nextDate = formatDate(next?.suggestedDate);
  const summary = result.reasoning ? (translated.reasoning ?? result.reasoning) : null;

  const channelLabel = knownLabel(t, 'channel', result.optimalChannel, KNOWN_CHANNELS);
  const urgencyLabel = knownLabel(t, 'urgency', result.urgency, KNOWN_URGENCIES);

  const stepLines = steps.slice(0, MAX_LIST_ITEMS).map((step, index) => {
    const when = [formatDate(step.suggestedDate), step.suggestedTime].filter(Boolean).join(' ');
    const channel = knownLabel(t, 'channel', step.type, KNOWN_CHANNELS);
    const head = [channel, when].filter(Boolean).join(' \u00b7 ');
    const why = stepReasons[index];
    return why ? `${head} \u2014 ${why}` : head;
  });

  return (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center">
        {channelLabel && (
          <Badge color="purple" variant="subtle" size="sm">
            {channelLabel}
          </Badge>
        )}
        {urgencyLabel && (
          <Badge color="amber" variant="subtle" size="sm">
            {urgencyLabel}
          </Badge>
        )}
        {nextDate && (
          <Text variant="caption" color={colors.text.tertiary}>
            {[nextDate, next?.suggestedTime].filter(Boolean).join(' ')}
          </Text>
        )}
      </HStack>
      {summary && (
        <Text variant="caption" color={colors.text.secondary} numberOfLines={3}>
          {summary}
        </Text>
      )}
      {stepLines.length > 0 ? (
        <BulletList items={stepLines} prefix="step" />
      ) : (
        <EmptyResult messageKey="agents.insights.followUpNoSteps" />
      )}
    </VStack>
  );
}

function AgentSection({
  type,
  labelKey,
  icon: Icon,
  iconColor,
  status,
  degraded,
  result,
  onRerun,
  isRerunning,
  pipelineRunning,
}: Readonly<{
  type: AgentType;
  labelKey: string;
  icon: typeof Target;
  iconColor: string;
  status: AgentExecutionStatus | null;
  degraded: boolean;
  result: QualificationResult | EnrichmentResult | SentimentResult | FollowUpResult | null;
  onRerun: () => void;
  isRerunning: boolean;
  pipelineRunning: boolean;
}>) {
  const { t } = useTranslation();
  const showResult = result && !pipelineRunning && !isRerunning;
  const [expanded, setExpanded] = useState(!!showResult);
  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <Box style={styles.agentRow}>
      <VStack spacing="sm">
        <Box
          pressable
          onPress={() => setExpanded(!expanded)}
          accessibilityRole="button"
          accessibilityLabel={t(labelKey)}
          accessibilityState={{ expanded }}
        >
          <HStack spacing="sm" align="center" justify="between">
            <HStack spacing="md" align="center" style={{ flex: 1, minWidth: 0 }}>
              {/* The agent's identity used to be a border and a tinted slab.
                  Four of those inside an already-bordered card read as four
                  competing cards; the hue now lives in the icon well alone. */}
              <Box
                backgroundColor={showResult ? withAlpha(iconColor, 0.16) : colors.surface.iconWell}
                borderRadius="md"
                alignItems="center"
                justifyContent="center"
                style={styles.iconBox}
              >
                <Icon size={18} color={iconColor} strokeWidth={2.25} />
              </Box>
              <Text
                variant="bodySmall"
                color={colors.text.primary}
                style={{ fontWeight: '700' }}
                numberOfLines={1}
              >
                {t(labelKey)}
              </Text>
              <ChevronIcon size={16} color={colors.text.tertiary} strokeWidth={2} />
            </HStack>
            <HStack spacing="sm" align="center" style={{ flexShrink: 0 }}>
              <StatusBadge status={status} degraded={degraded} />
              <Box
                pressable
                onPress={onRerun}
                testID={`rerun-${type}`}
                accessibilityRole="button"
                accessibilityLabel={t('agents.insights.rerun', { agent: t(labelKey) })}
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                style={styles.rerunButton}
              >
                {isRerunning ? (
                  <ActivityIndicator size={12} color={colors.text.tertiary} />
                ) : (
                  <RefreshCw size={14} color={colors.text.tertiary} strokeWidth={2} />
                )}
              </Box>
            </HStack>
          </HStack>
        </Box>

        {/* The badge says "rule-based"; this says what that means for the number
            the operator is about to act on. */}
        {degraded && status === AgentExecutionStatus.COMPLETED && (
          <Text variant="caption" color={colors.status.warning.main} style={styles.resultContent}>
            {t('agents.insights.ruleBasedHint')}
          </Text>
        )}

        {expanded && showResult && (
          <Box style={styles.resultContent}>
            {type === AgentType.LEAD_QUALIFICATION && (
              <QualificationContent result={result as QualificationResult} />
            )}
            {type === AgentType.LEAD_ENRICHMENT && (
              <EnrichmentContent result={result as EnrichmentResult} />
            )}
            {type === AgentType.SENTIMENT_ANALYSIS && (
              <SentimentContent result={result as SentimentResult} />
            )}
            {type === AgentType.FOLLOW_UP_SCHEDULER && (
              <FollowUpContent result={result as FollowUpResult} />
            )}
          </Box>
        )}

        {expanded &&
          !showResult &&
          !pipelineRunning &&
          status !== AgentExecutionStatus.RUNNING &&
          status !== AgentExecutionStatus.QUEUED && (
            <Text variant="caption" color={colors.text.tertiary} style={styles.resultContent}>
              {t('agents.insights.noResults')}
            </Text>
          )}
      </VStack>
    </Box>
  );
}

export function AgentInsightsCard({ leadId, organizationId }: Readonly<AgentInsightsCardProps>) {
  const { t } = useTranslation();
  const { data: status, isLoading, refetch } = useLeadAgentStatus(leadId, organizationId);
  const executePipeline = useExecutePipeline();
  const cancelPipeline = useCancelLeadPipeline();
  const executeAgent = useExecuteAgent();
  const [rerun, setRerun] = useState<{ agentType: AgentType; baselineId: string | null } | null>(
    null,
  );

  useRefetchOnRegainFocus(!!rerun, refetch);

  const executions = status?.executions;
  const rerunSettled = !!rerun && !!executions && isRerunSettled(rerun, executions);

  useEffect(() => {
    if (rerunSettled) setRerun(null);
  }, [rerunSettled]);

  if (isLoading) {
    return <SkeletonCard style={styles.skeleton} />;
  }

  const handleRunAll = () => {
    executePipeline.mutate({ organizationId, leadId });
  };

  const handleCancel = () => {
    setRerun(null);
    cancelPipeline.mutate({ organizationId, leadId });
  };

  const handleRerun = (agentType: AgentType) => {
    setRerun({
      agentType,
      baselineId: latestExecution(agentType, status?.executions ?? [])?.id ?? null,
    });
    executeAgent.mutate({ organizationId, agentType, leadId }, { onError: () => setRerun(null) });
  };

  const isRunning =
    executePipeline.isPending || cancelPipeline.isPending || !!status?.pipelineRunning;

  return (
    <VStack spacing="md">
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="sm" align="center">
          <Sparkles size={20} color={colors.primary.neon} strokeWidth={2.25} />
          <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '700' }}>
            {t('agents.insights.title')}
          </Text>
        </HStack>
        {isRunning ? (
          <Box
            pressable
            onPress={handleCancel}
            testID="cancel-pipeline-button"
            accessibilityLabel={t('agents.insights.cancelPipeline')}
            borderRadius="full"
            backgroundColor={colors.status.error.main}
            style={styles.runAllButton}
          >
            <HStack spacing="sm" align="center">
              <Square size={12} color={colors.white} strokeWidth={2.5} fill={colors.white} />
              <Text variant="bodySmall" color={colors.white} style={{ fontWeight: '700' }}>
                {t('agents.insights.cancel')}
              </Text>
            </HStack>
          </Box>
        ) : (
          <Box
            pressable
            onPress={handleRunAll}
            testID="run-all-button"
            borderRadius="full"
            backgroundColor={colors.primary[500]}
            style={styles.runAllButton}
          >
            <HStack spacing="sm" align="center">
              <Play
                size={14}
                color={colors['primary-foreground']}
                strokeWidth={2.5}
                fill={colors['primary-foreground']}
              />
              <Text
                variant="bodySmall"
                color={colors['primary-foreground']}
                style={{ fontWeight: '700' }}
              >
                {t('agents.insights.runAll')}
              </Text>
            </HStack>
          </Box>
        )}
      </HStack>

      {status?.pipelineRunning && (
        <Box backgroundColor={colors.primary.muted} borderRadius="md" padding="sm">
          <HStack spacing="sm" align="center" justify="center">
            <ActivityIndicator size={14} color={colors.primary[500]} />
            <Text variant="caption" color={colors.primary[400]} style={{ fontWeight: '600' }}>
              {t('agents.insights.generating')}
            </Text>
          </HStack>
        </Box>
      )}

      <VStack spacing="none">
        {AGENT_SECTIONS.map((section, index) => {
          const resultMap = status?.latestResults;
          const result = resultMap ? resultMap[section.resultKey] : null;
          const execStatus = status ? getExecutionStatus(section.type, status.executions) : null;
          const sectionRerunning = rerun?.agentType === section.type && !rerunSettled;
          const degraded =
            status && !sectionRerunning ? isDegraded(section.type, status.executions) : false;
          // A populated result can exist in customFields without a matching
          // AgentExecution row (FLOSEAI-599) — the badge must not say "Not run"
          // while the body underneath it shows a completed analysis.
          const effectiveStatus = sectionRerunning
            ? AgentExecutionStatus.RUNNING
            : (execStatus ?? (result ? AgentExecutionStatus.COMPLETED : null));

          return (
            <Fragment key={section.type}>
              {index > 0 && <Box style={listDivider} />}
              <AgentSection
                type={section.type}
                labelKey={section.labelKey}
                icon={section.icon}
                iconColor={section.iconColor}
                status={effectiveStatus}
                degraded={degraded}
                result={result}
                onRerun={() => handleRerun(section.type)}
                isRerunning={sectionRerunning}
                pipelineRunning={!!status?.pipelineRunning}
              />
            </Fragment>
          );
        })}
      </VStack>
    </VStack>
  );
}

const styles = StyleSheet.create({
  skeleton: {
    minHeight: 200,
  },
  agentRow: {
    paddingVertical: tokens.spacing.md,
  },
  iconBox: {
    width: 36,
    height: 36,
  },
  scoreBadge: {
    minWidth: 34,
    height: 30,
    paddingHorizontal: tokens.spacing.sm,
  },
  scoreText: {
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: tokens.fontSize.basePlus,
    fontWeight: '600',
  },
  rerunButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.surface.row,
  },
  // No glow here. The card itself already carries the one halo this view is
  // allowed; a second glowing element makes neither of them mean anything.
  runAllButton: {
    paddingHorizontal: tokens.spacing.mdPlus,
    paddingVertical: tokens.spacing.sm,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resultContent: {
    paddingLeft: 48,
  },
});
